// Copyright 2025-2026 Florian Gallon. All Rights reserved.
import {
  collection,
  query,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  where,
  orderBy,
  addDoc,
  Timestamp,
  deleteDoc
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';

/**
 * Default exchange rates to USD (used as fallback)
 */
const DEFAULT_EXCHANGE_RATES = {
  'USD': 1,
  'EUR': 1.08,
  'GBP': 1.27,
  'CAD': 0.74,
  '$': 1,
  '€': 1.08,
  '£': 1.27
};

/**
 * Get exchange rates from Firestore settings
 * Falls back to default rates if not configured
 * @returns {Promise<Object>} Exchange rates object
 */
export async function getExchangeRates() {
  try {
    const settingsDoc = await getDoc(doc(db, 'adminSettings', 'exchangeRates'));
    if (settingsDoc.exists()) {
      const data = settingsDoc.data();
      return {
        ...DEFAULT_EXCHANGE_RATES,
        ...data.rates,
        updatedAt: data.updatedAt
      };
    }
    return DEFAULT_EXCHANGE_RATES;
  } catch (error) {
    console.error('Error fetching exchange rates:', error);
    return DEFAULT_EXCHANGE_RATES;
  }
}

/**
 * Save exchange rates to Firestore settings
 * @param {Object} rates - Exchange rates object (e.g., { EUR: 1.08, GBP: 1.27, CAD: 0.74 })
 * @param {string} adminUserId - The admin user ID performing the action
 * @returns {Promise<void>}
 */
export async function saveExchangeRates(rates, adminUserId) {
  try {
    const settingsRef = doc(db, 'adminSettings', 'exchangeRates');
    await setDoc(settingsRef, {
      rates: {
        ...rates,
        // Always ensure USD is 1 and symbol mappings are consistent
        'USD': 1,
        '$': 1,
        '€': rates.EUR || rates['€'] || DEFAULT_EXCHANGE_RATES.EUR,
        '£': rates.GBP || rates['£'] || DEFAULT_EXCHANGE_RATES.GBP
      },
      updatedAt: Timestamp.now(),
      updatedBy: adminUserId
    }, { merge: true });

    // Log admin action
    await logAdminAction(adminUserId, 'updateExchangeRates', 'exchangeRates', {
      rates
    });
  } catch (error) {
    console.error('Error saving exchange rates:', error);
    throw error;
  }
}

/**
 * Get all bakeries (admin only - no bakeryId filter)
 * @param {string} statusFilter - 'all', 'active', or 'archived'
 * @param {Object} options - Additional options
 * @param {boolean} options.includeStats - Whether to include stats (totalOrders, totalRevenue)
 * @returns {Promise<Array>} Array of bakery objects with owner info
 */
export async function getAllBakeries(statusFilter = 'all', options = {}) {
  const { includeStats = false } = options;

  try {
    let q;

    // Build query based on status filter
    if (statusFilter === 'archived') {
      // Only fetch archived bakeries
      q = query(
        collection(db, 'bakeries'),
        where('status', '==', 'archived'),
        orderBy('createdAt', 'desc')
      );
    } else {
      // Fetch all bakeries (we'll filter client-side for 'active')
      q = query(
        collection(db, 'bakeries'),
        orderBy('createdAt', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    const bakeries = [];

    for (const docSnap of snapshot.docs) {
      const bakeryData = { id: docSnap.id, ...docSnap.data() };

      // Filter client-side for active status
      // Active = status is not 'archived' (includes undefined/null status)
      if (statusFilter === 'active' && bakeryData.status === 'archived') {
        continue; // Skip archived bakeries when filtering for active
      }

      // Fetch owner information
      if (bakeryData.ownerId) {
        try {
          const ownerDoc = await getDoc(doc(db, 'users', bakeryData.ownerId));
          if (ownerDoc.exists()) {
            bakeryData.ownerInfo = ownerDoc.data();
          }
        } catch (error) {
          console.error(`Failed to fetch owner info for bakery ${docSnap.id}:`, error);
        }
      }

      // Fetch stats if requested
      if (includeStats) {
        try {
          bakeryData.stats = await getBakeryStats(docSnap.id);
        } catch (error) {
          console.error(`Failed to fetch stats for bakery ${docSnap.id}:`, error);
          bakeryData.stats = { totalOrders: 0, totalRevenue: 0 };
        }
      }

      bakeries.push(bakeryData);
    }

    await attachLastSeen(bakeries);

    return bakeries;
  } catch (error) {
    console.error('Error fetching all bakeries:', error);
    throw error;
  }
}

/**
 * Attach lastSignInAt (Firebase Auth) and lastSeenAt (most recent of that and
 * ownerInfo.lastActiveAt) to each bakery. If the callable fails, lastSeenAt
 * falls back to lastActiveAt alone so the page still loads.
 * @param {Array} bakeries - Bakeries with ownerInfo already loaded (mutated in place)
 */
async function attachLastSeen(bakeries) {
  const ownerIds = [...new Set(bakeries.map(b => b.ownerId).filter(Boolean))];
  const signInTimes = {};

  try {
    const getBakerSignInTimes = httpsCallable(functions, 'getBakerSignInTimes');
    for (let i = 0; i < ownerIds.length; i += 100) {
      const result = await getBakerSignInTimes({ uids: ownerIds.slice(i, i + 100) });
      Object.assign(signInTimes, result.data?.times || {});
    }
  } catch (error) {
    console.error('Failed to fetch baker sign-in times:', error);
  }

  for (const bakery of bakeries) {
    bakery.lastSignInAt = signInTimes[bakery.ownerId] || null;
    const candidates = [bakery.ownerInfo?.lastActiveAt, bakery.lastSignInAt]
      .filter(Boolean)
      .map(d => new Date(d).getTime())
      .filter(ms => !isNaN(ms));
    bakery.lastSeenAt = candidates.length ? new Date(Math.max(...candidates)).toISOString() : null;
  }
}

/**
 * Get detailed information about a specific bakery
 * @param {string} bakeryId - The bakery ID
 * @returns {Promise<Object>} Bakery details with stats
 */
export async function getBakeryDetails(bakeryId) {
  try {
    const bakeryDoc = await getDoc(doc(db, 'bakeries', bakeryId));

    if (!bakeryDoc.exists()) {
      throw new Error('Bakery not found');
    }

    const bakeryData = { id: bakeryDoc.id, ...bakeryDoc.data() };

    // Fetch owner information
    if (bakeryData.ownerId) {
      const ownerDoc = await getDoc(doc(db, 'users', bakeryData.ownerId));
      if (ownerDoc.exists()) {
        bakeryData.ownerInfo = ownerDoc.data();
      }
    }

    // Fetch stats
    bakeryData.stats = await getBakeryStats(bakeryId);

    return bakeryData;
  } catch (error) {
    console.error('Error fetching bakery details:', error);
    throw error;
  }
}

/**
 * Get statistics for a specific bakery
 * @param {string} bakeryId - The bakery ID
 * @returns {Promise<Object>} Bakery statistics
 */
export async function getBakeryStats(bakeryId) {
  try {
    const stats = {
      totalProducts: 0,
      totalOrders: 0,
      totalRevenue: 0,
      activeOrders: 0
    };

    // Count products
    const productsQuery = query(
      collection(db, 'products'),
      where('bakeryId', '==', bakeryId)
    );
    const productsSnapshot = await getDocs(productsQuery);
    stats.totalProducts = productsSnapshot.size;

    // Count orders and calculate revenue
    const ordersQuery = query(
      collection(db, 'orders'),
      where('bakeryId', '==', bakeryId)
    );
    const ordersSnapshot = await getDocs(ordersQuery);
    stats.totalOrders = ordersSnapshot.size;

    ordersSnapshot.forEach(doc => {
      const orderData = doc.data();
      if (orderData.total) {
        stats.totalRevenue += orderData.total;
      }
      if (orderData.status && orderData.status !== 'completed') {
        stats.activeOrders++;
      }
    });

    return stats;
  } catch (error) {
    console.error('Error fetching bakery stats:', error);
    throw error;
  }
}

/**
 * Get all orders for a specific bakery
 * @param {string} bakeryId - The bakery ID
 * @param {number} limit - Maximum number of orders to fetch
 * @returns {Promise<Array>} Array of order objects
 */
export async function getBakeryOrders(bakeryId, limit = 50) {
  try {
    const ordersQuery = query(
      collection(db, 'orders'),
      where('bakeryId', '==', bakeryId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(ordersQuery);
    const orders = [];

    let count = 0;
    snapshot.forEach(doc => {
      if (count < limit) {
        orders.push({ id: doc.id, ...doc.data() });
        count++;
      }
    });

    return orders;
  } catch (error) {
    console.error('Error fetching bakery orders:', error);
    throw error;
  }
}

/**
 * Get all products for a specific bakery
 * @param {string} bakeryId - The bakery ID
 * @returns {Promise<Array>} Array of product objects
 */
export async function getBakeryProducts(bakeryId) {
  try {
    const productsQuery = query(
      collection(db, 'products'),
      where('bakeryId', '==', bakeryId)
    );

    const snapshot = await getDocs(productsQuery);
    const products = [];

    snapshot.forEach(doc => {
      products.push({ id: doc.id, ...doc.data() });
    });

    return products;
  } catch (error) {
    console.error('Error fetching bakery products:', error);
    throw error;
  }
}

/**
 * Archive a bakery (soft delete)
 * @param {string} bakeryId - The bakery ID
 * @param {string} adminUserId - The admin user ID performing the action
 * @returns {Promise<void>}
 */
export async function archiveBakery(bakeryId, adminUserId) {
  try {
    // Check for active orders first
    const stats = await getBakeryStats(bakeryId);
    if (stats.activeOrders > 0) {
      throw new Error('Cannot archive bakery with active orders');
    }

    // Update bakery status
    const bakeryRef = doc(db, 'bakeries', bakeryId);
    await updateDoc(bakeryRef, {
      status: 'archived',
      archivedAt: Timestamp.now(),
      archivedBy: adminUserId
    });

    // Log admin action
    await logAdminAction(adminUserId, 'archiveBakery', bakeryId, {
      reason: 'Admin archived bakery'
    });
  } catch (error) {
    console.error('Error archiving bakery:', error);
    throw error;
  }
}

/**
 * Restore an archived bakery
 * @param {string} bakeryId - The bakery ID
 * @param {string} adminUserId - The admin user ID performing the action
 * @returns {Promise<void>}
 */
export async function restoreBakery(bakeryId, adminUserId) {
  try {
    const bakeryRef = doc(db, 'bakeries', bakeryId);
    await updateDoc(bakeryRef, {
      status: 'active',
      restoredAt: Timestamp.now(),
      restoredBy: adminUserId
    });

    // Log admin action
    await logAdminAction(adminUserId, 'restoreBakery', bakeryId, {
      reason: 'Admin restored bakery'
    });
  } catch (error) {
    console.error('Error restoring bakery:', error);
    throw error;
  }
}

/**
 * Get all users (admin only)
 * @param {string} roleFilter - 'all', 'baker', 'customer', or 'admin'
 * @returns {Promise<Array>} Array of user objects
 */
export async function getAllUsers(roleFilter = 'all') {
  try {
    let q;

    if (roleFilter === 'all') {
      q = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc')
      );
    } else {
      q = query(
        collection(db, 'users'),
        where('role', '==', roleFilter),
        orderBy('createdAt', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    const users = [];

    snapshot.forEach(doc => {
      users.push({ id: doc.id, ...doc.data() });
    });

    return users;
  } catch (error) {
    console.error('Error fetching all users:', error);
    throw error;
  }
}

/**
 * Get system-wide statistics
 * @returns {Promise<Object>} System statistics
 */
export async function getSystemStats() {
  try {
    // Fetch configurable exchange rates from Firestore
    const exchangeRatesToUSD = await getExchangeRates();
    const stats = {
      totalBakeries: 0,
      activeBakeries: 0,
      archivedBakeries: 0,
      totalUsers: 0,
      totalOrders: 0,
      totalRevenueUSD: 0,
      bakerCount: 0,
      customerCount: 0
    };

    // Count bakeries and build a map of bakeryId -> currency info
    const bakeriesSnapshot = await getDocs(collection(db, 'bakeries'));
    stats.totalBakeries = bakeriesSnapshot.size;

    const bakeryCurrencyMap = {};
    bakeriesSnapshot.forEach(docSnap => {
      const data = docSnap.data();
      if (data.status === 'archived') {
        stats.archivedBakeries++;
      } else {
        stats.activeBakeries++;
      }
      // Store currency info for revenue conversion
      bakeryCurrencyMap[docSnap.id] = {
        currency: data.currency,
        currencySymbol: data.currencySymbol
      };
    });

    // Count users
    const usersSnapshot = await getDocs(collection(db, 'users'));
    stats.totalUsers = usersSnapshot.size;

    usersSnapshot.forEach(docSnap => {
      const data = docSnap.data();
      if (data.role === 'baker') {
        stats.bakerCount++;
      } else if (data.role === 'customer') {
        stats.customerCount++;
      }
    });

    // Count orders and calculate total revenue in USD
    const ordersSnapshot = await getDocs(collection(db, 'orders'));
    stats.totalOrders = ordersSnapshot.size;

    ordersSnapshot.forEach(docSnap => {
      const orderData = docSnap.data();
      if (orderData.total && orderData.bakeryId) {
        const bakeryInfo = bakeryCurrencyMap[orderData.bakeryId] || {};
        // Try to determine the currency from bakery info
        const currency = bakeryInfo.currency || bakeryInfo.currencySymbol || 'USD';
        // Get exchange rate (try both currency code and symbol)
        const rate = exchangeRatesToUSD[currency] || exchangeRatesToUSD[bakeryInfo.currencySymbol] || 1;
        stats.totalRevenueUSD += (orderData.total * rate);
      }
    });

    // Round to 2 decimal places
    stats.totalRevenueUSD = Math.round(stats.totalRevenueUSD * 100) / 100;

    return stats;
  } catch (error) {
    console.error('Error fetching system stats:', error);
    throw error;
  }
}

/**
 * Log an admin action to the audit trail
 * @param {string} adminUserId - The admin user ID
 * @param {string} action - The action performed
 * @param {string} targetId - The target entity ID
 * @param {Object} details - Additional details
 * @returns {Promise<void>}
 */
export async function logAdminAction(adminUserId, action, targetId, details = {}) {
  try {
    await addDoc(collection(db, 'adminActions'), {
      adminId: adminUserId,
      action,
      targetId,
      details,
      timestamp: Timestamp.now()
    });
  } catch (error) {
    console.error('Error logging admin action:', error);
    // Don't throw - logging failure shouldn't block the main action
  }
}

/**
 * Get recent admin actions (audit log)
 * @param {number} limit - Maximum number of actions to fetch
 * @returns {Promise<Array>} Array of admin action objects
 */
export async function getAdminActions(limit = 100) {
  try {
    const actionsQuery = query(
      collection(db, 'adminActions'),
      orderBy('timestamp', 'desc')
    );

    const snapshot = await getDocs(actionsQuery);
    const actions = [];

    let count = 0;
    for (const docSnap of snapshot.docs) {
      if (count >= limit) break;

      const actionData = { id: docSnap.id, ...docSnap.data() };

      // Fetch admin user info
      if (actionData.adminId) {
        try {
          const adminDoc = await getDoc(doc(db, 'users', actionData.adminId));
          if (adminDoc.exists()) {
            actionData.adminInfo = adminDoc.data();
          }
        } catch (error) {
          console.error(`Failed to fetch admin info for action ${docSnap.id}:`, error);
        }
      }

      actions.push(actionData);
      count++;
    }

    return actions;
  } catch (error) {
    console.error('Error fetching admin actions:', error);
    throw error;
  }
}

/**
 * Check if a user has admin role
 * @param {string} userId - The user ID to check
 * @returns {Promise<boolean>} True if user is admin
 */
export async function isUserAdmin(userId) {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return false;
    }
    return userDoc.data().role === 'admin';
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

/**
 * Get all orders across all bakeries (admin only)
 * @param {Object} filters - Optional filters
 * @param {string} filters.bakeryId - Filter by bakery ID
 * @param {string} filters.status - Filter by order status
 * @param {Date} filters.startDate - Filter by start date
 * @param {Date} filters.endDate - Filter by end date
 * @param {number} limit - Maximum number of orders to fetch
 * @returns {Promise<Array>} Array of order objects with bakery and customer info
 */
export async function getAllOrders(filters = {}, limit = 100) {
  try {
    let q = query(collection(db, 'orders'));

    // Apply filters
    const constraints = [orderBy('createdAt', 'desc')];

    if (filters.bakeryId) {
      constraints.unshift(where('bakeryId', '==', filters.bakeryId));
    }

    if (filters.status) {
      constraints.unshift(where('status', '==', filters.status));
    }

    // Build query with constraints
    if (constraints.length > 0) {
      q = query(collection(db, 'orders'), ...constraints);
    }

    const snapshot = await getDocs(q);
    const orders = [];

    let count = 0;
    for (const docSnap of snapshot.docs) {
      if (count >= limit) break;

      const orderData = { id: docSnap.id, ...docSnap.data() };

      // Apply client-side date filtering
      if (filters.startDate || filters.endDate) {
        const orderDate = orderData.createdAt?.toDate ? orderData.createdAt.toDate() : new Date(orderData.createdAt);

        if (filters.startDate && orderDate < filters.startDate) {
          continue;
        }

        if (filters.endDate && orderDate > filters.endDate) {
          continue;
        }
      }

      // Fetch bakery information
      if (orderData.bakeryId) {
        try {
          const bakeryDoc = await getDoc(doc(db, 'bakeries', orderData.bakeryId));
          if (bakeryDoc.exists()) {
            orderData.bakeryInfo = bakeryDoc.data();
          }
        } catch (error) {
          console.error(`Failed to fetch bakery info for order ${docSnap.id}:`, error);
        }
      }

      // Fetch customer information
      if (orderData.userId) {
        try {
          const userDoc = await getDoc(doc(db, 'users', orderData.userId));
          if (userDoc.exists()) {
            orderData.customerInfo = userDoc.data();
          }
        } catch (error) {
          console.error(`Failed to fetch customer info for order ${docSnap.id}:`, error);
        }
      }

      orders.push(orderData);
      count++;
    }

    return orders;
  } catch (error) {
    console.error('Error fetching all orders:', error);
    throw error;
  }
}

/**
 * Send an admin notification to one or more bakeries.
 * Uses the same notifications collection as baker payment alerts:
 *   notifications/{bakeryId}/items/{notificationId}
 *
 * @param {Object} params
 * @param {'all'|'single'} params.target - 'all' bakeries or a single bakery
 * @param {string} [params.bakeryId] - Required when target === 'single'
 * @param {string} params.titleEn - English title
 * @param {string} params.titleFr - French title
 * @param {string} params.bodyEn - English body
 * @param {string} params.bodyFr - French body
 * @param {string} [params.severity] - 'info' | 'warning' | 'error'
 * @param {string} [params.adminUserId] - Admin user sending the notification
 */
export async function sendAdminNotification({
  target = 'all',
  bakeryId,
  titleEn,
  titleFr,
  bodyEn,
  bodyFr,
  severity = 'info',
  adminUserId
}) {
  try {
    if (target === 'single' && !bakeryId) {
      throw new Error('bakeryId is required when target is "single"');
    }

    const createdAt = Timestamp.now();
    let bakeryIds = [];

    if (target === 'single') {
      // bakeryId is expected to be the bakery ownerId for consistency
      bakeryIds = [bakeryId];
    } else {
      const bakeriesSnapshot = await getDocs(collection(db, 'bakeries'));
      // Use ownerId when available so notifications are stored under the same
      // key used elsewhere in the app (orders, announcements, etc.).
      bakeryIds = bakeriesSnapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return data.ownerId || docSnap.id;
      });
    }

    const payloadBase = {
      type: 'admin_broadcast',
      createdAt,
      read: false,
      severity,
      title_en: titleEn,
      title_fr: titleFr,
      body_en: bodyEn,
      body_fr: bodyFr,
      audience: target,
      sentByAdminId: adminUserId || null
    };

    const writePromises = bakeryIds.map((id) =>
      addDoc(collection(db, 'notifications', id, 'items'), {
        ...payloadBase,
        bakeryId: id
      })
    );

    await Promise.all(writePromises);

    if (adminUserId) {
      await logAdminAction(adminUserId, 'sendAdminNotification', target === 'single' ? bakeryId : 'all-bakeries', {
        titleEn,
        titleFr,
        severity,
        target
      });
    }
  } catch (error) {
    console.error('Error sending admin notification:', error);
    throw error;
  }
}

/**
 * Get recent admin-broadcast notifications (from adminActions log)
 * @param {number} limit - Max number of notifications
 * @param {boolean} includeArchived - Whether to include archived items
 * @returns {Promise<Array>} Array of notification log entries with bakery names
 */
export async function getAdminNotificationHistory(limit = 20, includeArchived = false) {
  try {
    const actionsQuery = query(
      collection(db, 'adminActions'),
      where('action', '==', 'sendAdminNotification'),
      orderBy('timestamp', 'desc')
    );

    const snapshot = await getDocs(actionsQuery);
    const history = [];

    let count = 0;
    for (const docSnap of snapshot.docs) {
      if (count >= limit) break;
      const data = { id: docSnap.id, ...docSnap.data() };

      if (!includeArchived && data.archived) {
        continue;
      }

      let bakeryName = null;
      let bakeryId = null;

      if (data.targetId && data.targetId !== 'all-bakeries') {
        bakeryId = data.targetId;
        // Try to find bakery by ownerId, fallback to doc id.
        let bakeryDoc = null;
        const ownerQuery = query(
          collection(db, 'bakeries'),
          where('ownerId', '==', bakeryId)
        );
        const ownerSnap = await getDocs(ownerQuery);
        if (!ownerSnap.empty) {
          bakeryDoc = ownerSnap.docs[0];
        } else {
          const directDoc = await getDoc(doc(db, 'bakeries', bakeryId));
          if (directDoc.exists()) {
            bakeryDoc = directDoc;
          }
        }

        if (bakeryDoc) {
          const bakeryData = bakeryDoc.data();
          bakeryName = bakeryData.businessName || bakeryData.name || bakeryDoc.id;
        }
      }

      history.push({
        id: data.id,
        timestamp: data.timestamp,
        targetId: data.targetId,
        bakeryName,
        details: data.details || {},
        archived: !!data.archived,
        adminId: data.adminId
      });

      count++;
    }

    return history;
  } catch (error) {
    console.error('Error fetching admin notification history:', error);
    throw error;
  }
}

/**
 * Archive a previously sent admin notification (adminActions entry)
 * @param {string} actionId
 * @param {boolean} archived
 */
export async function setAdminNotificationArchived(actionId, archived) {
  try {
    const ref = doc(db, 'adminActions', actionId);
    await updateDoc(ref, {
      archived,
      archivedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error archiving admin notification:', error);
    throw error;
  }
}

/**
 * Grant grandfathered (lifetime free access) status to a bakery
 * @param {string} bakeryId - The bakery ID
 * @param {string} adminUserId - The admin user ID performing the action
 * @returns {Promise<void>}
 */
export async function grantGrandfatheredStatus(bakeryId, adminUserId) {
  try {
    const bakeryRef = doc(db, 'bakeries', bakeryId);
    await updateDoc(bakeryRef, {
      isGrandfathered: true,
      grandfatheredAt: Timestamp.now(),
      grandfatheredBy: adminUserId
    });

    // Log admin action
    await logAdminAction(adminUserId, 'grantGrandfatheredStatus', bakeryId, {
      reason: 'Admin granted lifetime free access'
    });
  } catch (error) {
    console.error('Error granting grandfathered status:', error);
    throw error;
  }
}

/**
 * Revoke grandfathered (lifetime free access) status from a bakery
 * @param {string} bakeryId - The bakery ID
 * @param {string} adminUserId - The admin user ID performing the action
 * @returns {Promise<void>}
 */
export async function revokeGrandfatheredStatus(bakeryId, adminUserId) {
  try {
    const bakeryRef = doc(db, 'bakeries', bakeryId);
    await updateDoc(bakeryRef, {
      isGrandfathered: false,
      grandfatheredRevokedAt: Timestamp.now(),
      grandfatheredRevokedBy: adminUserId
    });

    // Log admin action
    await logAdminAction(adminUserId, 'revokeGrandfatheredStatus', bakeryId, {
      reason: 'Admin revoked lifetime free access'
    });
  } catch (error) {
    console.error('Error revoking grandfathered status:', error);
    throw error;
  }
}

/**
 * Delete an admin notification entirely:
 * - Remove its adminActions log entry
 * - Remove related admin_broadcast docs from baker notifications collections
 *
 * NOTE: Matching is best-effort based on title and timestamp.
 * @param {string} actionId
 */
export async function deleteAdminNotification(actionId) {
  try {
    const actionRef = doc(db, 'adminActions', actionId);
    const actionSnap = await getDoc(actionRef);
    if (!actionSnap.exists()) {
      return;
    }

    const action = actionSnap.data();
    const details = action.details || {};
    const titleEn = details.titleEn || details.titleFr || '';
    const timestamp = action.timestamp;

    // Helper to delete matching notifications for one bakery ownerId
    const deleteForOwner = async (ownerId) => {
      if (!ownerId) return;
      const notifCol = collection(db, 'notifications', ownerId, 'items');
      const q = query(
        notifCol,
        where('type', '==', 'admin_broadcast'),
        where('title_en', '==', titleEn)
      );
      const snap = await getDocs(q);

      const thresholdMs = 5 * 60 * 1000; // 5 minutes window
      const tsMillis = timestamp && timestamp.toMillis ? timestamp.toMillis() : null;

      const deletes = [];
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        const created = data.createdAt && data.createdAt.toMillis ? data.createdAt.toMillis() : null;
        const withinWindow =
          tsMillis == null || created == null ? true : Math.abs(created - tsMillis) <= thresholdMs;
        if (withinWindow) {
          deletes.push(deleteDoc(docSnap.ref));
        }
      });

      await Promise.all(deletes);
    };

    if (action.targetId === 'all-bakeries') {
      const bakeriesSnapshot = await getDocs(collection(db, 'bakeries'));
      const ownerIds = bakeriesSnapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return data.ownerId || docSnap.id;
      });
      await Promise.all(ownerIds.map((ownerId) => deleteForOwner(ownerId)));
    } else if (action.targetId) {
      await deleteForOwner(action.targetId);
    }

    await deleteDoc(actionRef);
  } catch (error) {
    console.error('Error deleting admin notification:', error);
    throw error;
  }
}
